import { TextFilterArtifactsMatchMode } from '@contember/react-dataview'
import { InviteErrorCodes } from './tenant/hooks/useInvite'
import { UploaderErrorType } from '@contember/react-uploader'
import { ChangeMyPasswordFormErrorCode, CreateApiKeyFormErrorCode } from '@contember/interface'
import {
	InviteFormErrorCode,
	LoginFormErrorCode,
	OtpPrepareFormErrorCode,
	OtpConfirmFormErrorCode,
	PasswordResetFormErrorCode,
	PasswordResetRequestFormErrorCode,
	UpdateProjectMemberFormErrorCode,
	PasswordlessSignInInitFormErrorCode,
	PasswordlessSignInFormErrorCode,
} from '@contember/react-identity'
import { ActivatePasswordlessOtpErrorCode } from '@contember/graphql-client-tenant'

export const dict = {
	deleteEntityDialog: {
		title: 'Jste si naprosto jisti?',
		description: 'Tuto akci nelze vrátit zpět.',
		confirmButton: 'Smazat',
		cancelButton: 'Zrušit',
	},
	identityLoader: {
		fail: 'Nepodařilo se načíst identitu',
	},
	persist: {
		persistButton: 'Uložit data',
		invalidInputError: 'Nepodařilo se uložit data',
		invalidResponseError: 'Neplatná odpověď',
		invalidResponseErrorDetails: 'Server vrátil neplatnou odpověď. Zkuste to prosím později.',
		success: 'Úspěšně uloženo',
		afterPersistError: 'Po uložení dat došlo k chybě. Obnovte prosím stránku.',
	},
	toast: {
		showDetails: 'Zobrazit detaily',
	},
	datagrid: {
		na: 'N/A',
		dateStart: 'Od',
		today: 'Dnes',
		dateEnd: 'Do',
		numberFrom: 'Od',
		numberTo: 'Do',
		textReset: 'Resetovat filtr',
		textPlaceholder: 'Hledat',
		textMatchMode: {
			'matches': 'obsahuje',
			'matchesExactly': 'rovná se',
			'startsWith': 'začíná na',
			'endsWith': 'končí na',
			'doesNotMatch': 'neobsahuje',
		} satisfies Record<TextFilterArtifactsMatchMode, string>,
		visibleFields: 'Pole',
		columnAsc: 'vzestupně',
		columnDesc: 'sestupně',
		columnHide: 'Skrýt',
		empty: 'Žádné výsledky.',
		layout: 'Rozvržení',
		showGrid: 'Mřížka',
		showTable: 'Tabulka',
		paginationFirstPage: 'První stránka',
		paginationPreviousPage: 'Předchozí stránka',
		paginationNextPage: 'Další stránka',
		paginationLastPage: 'Poslední stránka',
		paginationRowsPerPage: 'Řádků na stránku',

		pageInfo: 'Stránka ${page} z ${pagesCount}',
		pageInfoShort: 'Stránka ${page}',
		pageRowsCount: '${totalCount} celkem řádků',

		filter: 'Filtr',
		filters: 'Filtry',
		exclude: 'Vyloučit',
		export: 'Exportovat',
	},
	select: {
		confirmNew: 'Potvrdit',
		cancelNew: 'Zrušit',
		search: 'Hledat',
		paginationPreviousPage: 'Předchozí stránka',
		paginationNextPage: 'Další stránka',
		placeholder: 'Vybrat',
	},
	backButton: {
		back: 'Zpět',
	},

	errors: {
		required: 'Toto pole je povinné',
		unique: 'Tato hodnota je již obsazena',
		unknown: 'Došlo k neznámé chybě',
	},

	boolean: {
		true: 'Ano',
		false: 'Ne',
	},
	logout: 'Odhlásit se',
	inviteErrors: {
		ALREADY_MEMBER: 'Tento uživatel je již členem projektu',
		INVALID_EMAIL_FORMAT: 'Neplatný formát e-mailu',
		INVALID_MEMBERSHIP: 'Neplatné členství',
		PROJECT_NOT_FOUND: 'Projekt nenalezen',
		ROLE_NOT_FOUND: 'Role nenalezena',
		VARIABLE_EMPTY: 'Proměnná je prázdná',
		VARIABLE_NOT_FOUND: 'Proměnná nenalezena',
		fallback: 'Nepodařilo se pozvat uživatele',
	} satisfies Record<InviteErrorCodes | 'fallback', string>,
	uploader: {
		uploadErrors: {
			httpError: 'HTTP chyba',
			aborted: 'Nahrávání přerušeno',
			networkError: 'Síťová chyba',
			timeout: 'Časový limit nahrávání',
			fileRejected: 'Soubor byl odmítnut',
		} satisfies Record<UploaderErrorType, string>,
		uploaderUnknownError: 'Neznámá chyba',

		browseFiles: 'Procházet',
		dropFiles: 'Pusťte soubory zde',
		or: 'nebo',
	},
	repeater: {
		empty: 'Žádné položky.',
		addItem: 'Přidat položku',
	},
	outdatedApplication: {
		title: 'K dispozici je nová verze',
		description: 'Pro přístup k nejnovějším funkcím a vylepšením prosím obnovte prohlížeč.',
		warning: 'Veškerá neuložená data budou ztracena. Před obnovením prosím uložte svou práci.',
		snooze: 'Odložit',
		refreshNow: 'Obnovit nyní',
	},
	tenant: {
		memberList: {
			failedToLoadData: 'Nepodařilo se načíst data',
			noResults: 'Žádné výsledky',
			previous: 'Předchozí',
			next: 'Další',
			roles: 'Role',
			action: 'Akce',
		},
		apiKeyList: {
			deleted: 'API klíč smazán',
			deleteFailed: 'Nepodařilo se smazat API klíč',
			unnamed: 'Nepojmenovaný API klíč',
			noResults: 'Žádné API klíče',
			description: 'Popis',
			role: 'Role',
			action: 'Akce',
			deleteConfirmation: 'Opravdu chcete smazat tento API klíč?',
			deleteCancelAction: 'Zrušit',
			deleteConfirmAction: 'Smazat',
		},
		createApiKey: {
			description: 'Popis',
			roles: 'Role',
			submit: 'Vytvořit API klíč',
			errorMessages: {
				FIELD_REQUIRED: 'Toto pole je povinné',
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
				INVALID_MEMBERSHIP: 'Neplatné členství',
				PROJECT_NOT_FOUND: 'Projekt nebyl nalezen',
				ROLE_NOT_FOUND: 'Role nebyla nalezena',
				VARIABLE_EMPTY: 'Proměnná je prázdná',
				VARIABLE_NOT_FOUND: 'Proměnná nebyla nalezena',
			} satisfies Record<CreateApiKeyFormErrorCode, string>,
		},
		invite: {
			email: 'E-mail',
			name: 'Jméno',
			roles: 'Role',
			submit: 'Pozvat',
			addRole: 'Přidat roli',
			errorMessages: {
				FIELD_REQUIRED: 'Toto pole je povinné',
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
				ALREADY_MEMBER: 'Tato osoba je již členem',
				INVALID_EMAIL_FORMAT: 'Neplatný formát e-mailu',
				INVALID_MEMBERSHIP: 'Neplatné členství',
				PROJECT_NOT_FOUND: 'Projekt nebyl nalezen',
				ROLE_NOT_FOUND: 'Role nebyla nalezena',
				VARIABLE_EMPTY: 'Proměnná je prázdná',
				VARIABLE_NOT_FOUND: 'Proměnná nebyla nalezena',
			} satisfies Record<InviteFormErrorCode, string>,
		},
		changePassword: {
			currentPassword: 'Současné heslo',
			newPassword: 'Nové heslo',
			confirmPassword: 'Potvrdit heslo',
			submit: 'Změnit heslo',
			errorMessages: {
				FIELD_REQUIRED: 'Toto pole je povinné',
				INVALID_VALUE: 'Neplatná hodnota',
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
				PASSWORD_MISMATCH: 'Hesla se neshodují',
				TOO_WEAK: 'Heslo je příliš slabé',
				NO_PASSWORD_SET: 'Váš účet nemá nastavené heslo',
				INVALID_PASSWORD: 'Neplatné heslo',
				NOT_A_PERSON: 'Neplatný uživatel',
			} satisfies Record<ChangeMyPasswordFormErrorCode, string>,
		},
		login: {
			email: 'E-mail',
			password: 'Heslo',
			otpToken: '2FA OTP token',
			forgotPassword: 'Zapomenuté heslo?',
			login: 'Přihlásit se',
			errorMessages: {
				FIELD_REQUIRED: 'Toto pole je povinné',
				INVALID_VALUE: 'Neplatná hodnota',
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
				INVALID_PASSWORD: 'Neplatné heslo',
				INVALID_OTP_TOKEN: 'Neplatný OTP token',
				NO_PASSWORD_SET: 'Není nastavené heslo',
				PERSON_DISABLED: 'Uživatel je deaktivován',
				UNKNOWN_EMAIL: 'Neznámý e-mail',
				OTP_REQUIRED: 'Je vyžadován OTP token',
				INVALID_CREDENTIALS: 'Neplatné přihlašovací údaje',
				RATE_LIMIT_EXCEEDED: 'Příliš mnoho pokusů. Zkuste to prosím později',
			} satisfies Record<LoginFormErrorCode, string>,
			idpInitError: 'Nepodařilo se inicializovat IdP přihlášení:',
			idpResponseError: 'Nepodařilo se zpracovat IdP odpověď:',
			title: 'Přihlášení',
			description: 'Zadejte svůj e-mail níže pro přihlášení k vašemu účtu',
			backToLogin: 'Zpět k přihlášení',
		},
		personList: {
			email: 'E-mail',
			name: 'Jméno',
			deleted: 'Člen smazán',
			deleteFailed: 'Nepodařilo se smazat člena',
			noEmail: 'Uživatel nemá e-mail',
			noName: 'Uživatel nemá jméno',
			role: 'Role',
			action: 'Akce',
			noResults: 'Žádní členové',
			deleteConfirmation: 'Opravdu chcete smazat tohoto člena?',
			deleteCancelAction: 'Zrušit',
			deleteConfirmAction: 'Smazat',
		},
		memberDelete: {
			cancel: 'Zrušit',
			confirm: 'Potvrdit',
		},
		otpSetup: {
			prepareContinue: 'Pokračovat',
			otpConfirmFormErrorMessages: {
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
				INVALID_OTP_TOKEN: 'Neplatný OTP token',
				FIELD_REQUIRED: 'Toto pole je povinné',
				NOT_PREPARED: 'Nastavení OTP nebylo inicializováno',
			} satisfies Record<OtpConfirmFormErrorCode, string>,
			otpPrepareFormErrorMessages: {
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
			} satisfies Record<OtpPrepareFormErrorCode, string>,
			alreadyHaveOtp: 'Máte již aktivní dvoufaktorovou autentizaci. Kliknutím na "Pokračovat" stará autentizace přestane fungovat.',
			nowScanQrCode: 'Nyní naskenujte následující QR kód ve vaší aplikaci:',
			writeDownCode: 'Po registraci do vaší aplikace si zapište kód z aplikace.',
			twoFactorEnabled: 'Dvoufaktorová autentizace JE AKTIVOVÁNA.',
			disableTwoFactor: 'Deaktivovat dvoufaktorovou autentizaci',
			disableTwoFactorConfirm: 'Opravdu chcete deaktivovat dvoufaktorovou autentizaci?',
			cancel: 'Zrušit',
			disable: 'Deaktivovat',
			disabledSuccess: 'Dvoufaktorová autentizace byla deaktivována.',
			setupAgain: 'Nastavit znovu',
			description:
				'Dvoufaktorová autentizace poskytuje silnější zabezpečení vašeho účtu tím, že vyžaduje druhý krok ověření při přihlášení. Kromě vašeho hesla budete také potřebovat kód generovaný aplikací ve vašem telefonu.',
			setupTwoFactor: 'Nastavit dvoufaktorovou autentizaci',
			enabledSuccess: 'Dvoufaktorová autentizace byla aktivována.',
			otpToken: 'OTP token',
			label: 'Štítek',
			confirm: 'Potvrdit',
		},

		passwordReset: {
			token: 'Resetovací kód',
			password: 'Heslo',
			passwordConfirmation: 'Potvrdit heslo',
			submit: 'Resetovat heslo',
			errorMessages: {
				FIELD_REQUIRED: 'Toto pole je povinné',
				INVALID_VALUE: 'Neplatná hodnota',
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
				PASSWORD_MISMATCH: 'Hesla se neshodují',
				PASSWORD_TOO_WEAK: 'Heslo je příliš slabé',
				TOKEN_EXPIRED: 'Token vypršel',
				TOKEN_NOT_FOUND: 'Token nebyl nalezen',
				TOKEN_USED: 'Token již byl použit',
				TOKEN_INVALID: 'Neplatný token',
			} satisfies Record<PasswordResetFormErrorCode, string>,
			title: 'Resetovat heslo',
			description: 'Zadejte nové heslo níže',
			success: 'Heslo bylo resetováno',
		},
		passwordResetRequest: {
			email: 'E-mail',
			submit: 'Požádat o resetovací kód',
			errorMessages: {
				FIELD_REQUIRED: 'Toto pole je povinné',
				INVALID_VALUE: 'Neplatná hodnota',
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
				PERSON_NOT_FOUND: 'Neznámý e-mail',
			} satisfies Record<PasswordResetRequestFormErrorCode, string>,
			title: 'Resetovat heslo',
			description: 'Zadejte svůj e-mail níže pro resetování hesla',
			success: 'Odkaz pro reset hesla byl odeslán',
			checkMail: 'Prosím zkontrolujte svou poštovní schránku pro instrukce, jak resetovat heslo.',
			entryCode: 'Nebo zadejte resetovací kód přímo.',
		},
		passwordlessSignInInit: {
			email: 'E-mail',
			submit: 'Odeslat magický odkaz',
			errorMessages: {
				FIELD_REQUIRED: 'Toto pole je povinné',
				INVALID_VALUE: 'Neplatná hodnota',
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
				PERSON_NOT_FOUND: 'Neznámý e-mail',
				PASSWORDLESS_DISABLED: 'Přihlášení bez hesla není dostupné',
			} satisfies Record<PasswordlessSignInInitFormErrorCode, string>,
			title: 'Přihlásit se',
			description: 'Zadejte svůj e-mail níže pro přihlášení k vašemu účtu',
			success: 'Magický odkaz byl odeslán',
			checkMail: 'Prosím zkontrolujte svou poštovní schránku pro instrukce, jak se přihlásit.',
			entryCode: 'Nebo zadejte resetovací kód přímo.',
		},
		passwordlessSignIn: {
			token: 'Ověřovací kód',
			otpToken: '2FA OTP token',
			submit: 'Přihlásit se',
			errorMessages: {
				FIELD_REQUIRED: 'Toto pole je povinné',
				INVALID_VALUE: 'Neplatná hodnota',
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
				INVALID_OTP_TOKEN: 'Neplatný OTP token',
				OTP_REQUIRED: undefined,
				PERSON_DISABLED: 'Uživatel je deaktivován',
				TOKEN_EXPIRED: 'Token vypršel',
				TOKEN_NOT_FOUND: 'Token nebyl nalezen',
				TOKEN_USED: 'Token již byl použit',
				TOKEN_INVALID: 'Neplatný token',
			} satisfies Record<PasswordlessSignInFormErrorCode, string | undefined>,
			title: 'Přihlásit se',
		},
		otpActivation: {
			errorMessages: {
				TOKEN_INVALID: 'Tento token není platný. Zkuste to prosím znovu.',
				TOKEN_USED: 'Token již byl použit',
				TOKEN_NOT_FOUND: 'Token nebyl nalezen',
				TOKEN_EXPIRED: 'Token vypršel',
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
			} satisfies Record<ActivatePasswordlessOtpErrorCode | 'UNKNOWN_ERROR', string>,
		},

		updateProjectMember: {
			updateSuccess: 'Role aktualizovány',
			roles: 'Role',
			submit: 'Aktualizovat roli',
			errorMessages: {
				FIELD_REQUIRED: 'Toto pole je povinné',
				UNKNOWN_ERROR: 'Něco se pokazilo. Zkuste to prosím znovu později',
				INVALID_MEMBERSHIP: 'Neplatné členství',
				PROJECT_NOT_FOUND: 'Projekt nebyl nalezen',
				ROLE_NOT_FOUND: 'Role nebyla nalezena',
				VARIABLE_EMPTY: 'Proměnná je prázdná',
				VARIABLE_NOT_FOUND: 'Proměnná nebyla nalezena',
				NOT_MEMBER: 'Nejste členem',
			} satisfies Record<UpdateProjectMemberFormErrorCode, string>,
		},
	},
}
export const dictFormat = (value: string, replacements: Record<string, string>) => {
	return value.replace(/\${([^}]+)}/g, (_, key) => replacements[key] || '')
}
